import { useState, useMemo } from 'react'
import { format, isToday } from 'date-fns'
import type { PortfolioSnapshot, Position, SignalState, RegimeState, StrategyConfig } from '../lib/supabase'

// ─── Types ──────────────────────────────────────────────────────────────────

interface Props {
  onOpenStrategy?: () => void
  snapshot:        PortfolioSnapshot | null
  regimeState:     RegimeState | null
  signals:         SignalState[]
  positions:       Position[]
  sleeveMap:       Record<string, string>
  isSignalsStale?: boolean
  strategyConfig?: StrategyConfig | null
}

type SleeveName = 'all' | 'etf' | 'stock' | 'ipo' | 'commod' | 'crypto'
type ScalarTone = 'strong-bull' | 'bull' | 'neutral' | 'cautious' | 'bear' | 'halted'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function scalarTone(s: number | null | undefined): ScalarTone {
  if (s == null) return 'neutral'
  if (s <= 0)    return 'halted'
  if (s >= 1.50) return 'strong-bull'
  if (s >= 1.25) return 'bull'
  if (s >= 1.00) return 'neutral'
  if (s >= 0.75) return 'cautious'
  return 'bear'
}

function cbScaleFromDrawdown(dd: number): { scalar: number; label: string; cashFloor: number } {
  if (dd >= 25) return { scalar: 0,    label: 'HALTED',     cashFloor: 50 }
  if (dd >= 20) return { scalar: 0.25, label: 'Severe DD',  cashFloor: 50 }
  if (dd >= 15) return { scalar: 0.50, label: 'Heavy DD',   cashFloor: 30 }
  if (dd >= 10) return { scalar: 0.75, label: 'Mild DD',    cashFloor: 15 }
  return              { scalar: 1.00, label: 'No drawdown', cashFloor: 5  }
}

function stockTierLabel(s: number | null | undefined): string {
  if (s == null) return '—'
  if (s >= 1.50) return 'Bull'
  if (s >= 1.25) return 'Favorable'
  if (s >= 1.00) return 'Neutral'
  if (s >= 0.75) return 'Cautious'
  if (s > 0)     return 'Bear'
  return 'Halted'
}

function ipoTierLabel(s: number | null | undefined): string {
  if (s == null) return '—'
  if (s >= 1.25) return 'IPO-Bull'
  if (s >= 1.00) return 'IPO-Neutral'
  if (s > 0)     return 'IPO-Bear'
  return 'IPO-Halted'
}

function commodTierLabel(s: number | null | undefined): string {
  if (s == null) return '—'
  if (s >= 1.25) return 'Commod-Bull'
  if (s >= 1.00) return 'Commod-Neutral'
  if (s > 0)     return 'Commod-Bear'
  return 'Commod-Halted'
}

function cryptoTierLabel(s: number | null | undefined): string {
  if (s == null) return '—'
  if (s >= 1.25) return 'Crypto-Bull'
  if (s >= 1.00) return 'Crypto-Neutral'
  if (s > 0)     return 'Crypto-Bear'
  return 'Crypto-Halted'
}

function percentToStop(p: Position): number | null {
  if (p.current_price == null || p.stop_price == null || p.current_price === 0) return null
  return ((p.current_price - p.stop_price) / p.current_price) * 100
}

function fmtMoney(n: number): string {
  return n >= 1000 ? `$${(n / 1000).toFixed(1)}K` : `$${n.toFixed(0)}`
}

function fmtPct(n: number, decimals = 1): string {
  return `${n >= 0 ? '' : ''}${n.toFixed(decimals)}%`
}

function fmtScalar(s: number | null | undefined): string {
  if (s == null) return '—'
  return `${s.toFixed(2)}×`
}

// ─── Sleeve config ────────────────────────────────────────────────────────────

const SLEEVE_OPTIONS: { id: SleeveName; label: string; ariaLabel: string }[] = [
  { id: 'all',    label: 'All Sleeves', ariaLabel: 'All sleeves — show all' },
  { id: 'etf',    label: 'ETF',         ariaLabel: 'ETF sleeve' },
  { id: 'stock',  label: 'Stock',       ariaLabel: 'Stock sleeve' },
  { id: 'ipo',    label: 'IPO',         ariaLabel: 'IPO sleeve' },
  { id: 'commod', label: 'Commod',      ariaLabel: 'Commodity sleeve' },
  { id: 'crypto', label: 'Crypto',      ariaLabel: 'Crypto sleeve' },
]

// ─── Source-linked universe filter builder ────────────────────────────────────
// Values are fetched live from jgl_strategy_config (populated by publish_strategy_config.py,
// which imports directly from the Python source files). Static fallback values are used when
// the config table has not yet been populated — they carry a `src` annotation pointing to the
// authoritative Python constant so any drift is immediately traceable.

interface FilterRow {
  name: string
  threshold: string
  src: string  // Python path: "file · VARIABLE['key']" — shown on row hover
}

interface SleeveUniverse {
  count: string
  filters: FilterRow[]
}

function fmtVol(n: number): string {
  return `≥ ${(n / 1000).toFixed(0)}K shares`
}

function fmtCap(n: number): string {
  if (n >= 1_000_000_000) return `≥ $${(n / 1_000_000_000).toFixed(0)}B`
  return `≥ $${(n / 1_000_000).toFixed(0)}M`
}

function buildUniverseFilters(config: StrategyConfig | null, atrPeriod: number): Record<string, SleeveUniverse> {
  const f = config?.universe_filters
  const atrLabel = `ATR(${atrPeriod})%`

  return {
    etf: {
      count: '~37 qualified / ~145 base',
      filters: [
        { name: 'Avg Daily Vol (50d)', threshold: f?.etf?.avg_volume_50d_min != null ? fmtVol(f.etf.avg_volume_50d_min) : '≥ 800K shares', src: "weekly_qualification_scan.py · FILTERS['etf']['avg_volume_50d_min']" },
        { name: 'AUM',                 threshold: f?.etf?.aum_min            != null ? fmtCap(f.etf.aum_min)            : '≥ $250M',        src: "weekly_qualification_scan.py · FILTERS['etf']['aum_min']" },
        { name: atrLabel,              threshold: f?.etf?.atr_pct_min        != null ? `≥ ${f.etf.atr_pct_min}%`        : '≥ 0.5%',         src: "weekly_qualification_scan.py · FILTERS['etf']['atr_pct_min']" },
        { name: 'Bid-Ask Spread',      threshold: f?.etf?.spread_pct_max     != null ? `≤ ${f.etf.spread_pct_max}%`     : '≤ 0.25%',        src: "weekly_qualification_scan.py · FILTERS['etf']['spread_pct_max']" },
        { name: 'Price',               threshold: f?.etf?.price_floor        != null ? `≥ $${f.etf.price_floor}`        : '≥ $5',           src: "weekly_qualification_scan.py · FILTERS['etf']['price_floor']" },
        { name: 'Inception',           threshold: f?.etf?.inception_days_min != null ? `≥ ${Math.round(f.etf.inception_days_min / 30.4)} months` : '≥ 18 months', src: "weekly_qualification_scan.py · FILTERS['etf']['inception_days_min']" },
        { name: 'Overnight Gap (20d)', threshold: f?.etf?.avg_gap_pct_max    != null ? `≤ ${f.etf.avg_gap_pct_max}%`   : '≤ 2.0%',         src: "weekly_qualification_scan.py · FILTERS['etf']['avg_gap_pct_max']" },
        { name: 'Leveraged / Inverse', threshold: 'Excluded',                                                                                  src: "weekly_qualification_scan.py · LEVERAGED_INVERSE_PATTERNS" },
      ],
    },
    stock: {
      count: '~147 qualified / ~620 base',
      filters: [
        { name: 'Market Cap',          threshold: f?.stock?.market_cap_min     != null ? fmtCap(f.stock.market_cap_min)   : '≥ $10B',         src: "weekly_qualification_scan.py · FILTERS['stock']['market_cap_min']" },
        { name: 'Avg Daily Vol (5d & 20d)', threshold: f?.stock?.avg_volume_5d_min != null ? fmtVol(f.stock.avg_volume_5d_min) : '≥ 750K shares', src: "weekly_qualification_scan.py · FILTERS['stock']['avg_volume_5d_min']" },
        { name: atrLabel,              threshold: f?.stock?.atr_pct_min != null && f?.stock?.atr_pct_max != null ? `${f.stock.atr_pct_min}–${f.stock.atr_pct_max}%` : '1–8%', src: "weekly_qualification_scan.py · FILTERS['stock']['atr_pct_min/max']" },
        { name: 'Price',               threshold: f?.stock?.price_floor        != null ? `≥ $${f.stock.price_floor}`     : '≥ $5',           src: "weekly_qualification_scan.py · FILTERS['stock']['price_floor']" },
        { name: 'Days since IPO',      threshold: f?.stock?.days_since_ipo_min != null ? `≥ ${Math.round(f.stock.days_since_ipo_min / 30.4)} months (${f.stock.days_since_ipo_min} days)` : '≥ 18 months (548 days)', src: "weekly_qualification_scan.py · FILTERS['stock']['days_since_ipo_min']" },
        { name: 'Exchange',            threshold: 'NYSE / NASDAQ only',                                                                         src: "weekly_qualification_scan.py · VALID_EXCHANGES" },
        { name: 'Security Type',       threshold: 'Common stock only',                                                                          src: "weekly_qualification_scan.py · VALID_STOCK_TYPES" },
        { name: 'Short Interest',      threshold: f?.stock?.short_interest_max != null ? `≤ ${f.stock.short_interest_max * 100}% of float`  : '≤ 50% of float',  src: "weekly_qualification_scan.py · FILTERS['stock']['short_interest_max']" },
        { name: 'Overnight Gap (20d)', threshold: f?.stock?.avg_gap_pct_max    != null ? `≤ ${f.stock.avg_gap_pct_max}%` : '≤ 3.0%',         src: "weekly_qualification_scan.py · FILTERS['stock']['avg_gap_pct_max']" },
      ],
    },
    ipo: {
      count: '~4–8 qualified (rolling cohort)',
      filters: [
        { name: 'Days since IPO',      threshold: f?.ipo?.days_since_ipo_min != null && f?.ipo?.days_since_ipo_max != null ? `${f.ipo.days_since_ipo_min} days – ${Math.round(f.ipo.days_since_ipo_max / 30.4)} months (${f.ipo.days_since_ipo_max} days)` : '21 days – 18 months (548 days)', src: "weekly_qualification_scan.py · FILTERS['ipo']['days_since_ipo_min/max']" },
        { name: 'Avg Daily Vol (10d)', threshold: f?.ipo?.avg_volume_10d_min != null ? fmtVol(f.ipo.avg_volume_10d_min) : '≥ 750K shares',   src: "weekly_qualification_scan.py · FILTERS['ipo']['avg_volume_10d_min']" },
        { name: 'Price',               threshold: f?.ipo?.price_floor        != null ? `≥ $${f.ipo.price_floor}`        : '≥ $10',           src: "weekly_qualification_scan.py · FILTERS['ipo']['price_floor']" },
        { name: 'Market Cap',          threshold: f?.ipo?.market_cap_min     != null ? fmtCap(f.ipo.market_cap_min)     : '≥ $750M',         src: "weekly_qualification_scan.py · FILTERS['ipo']['market_cap_min']" },
        { name: atrLabel,              threshold: f?.ipo?.atr_pct_min != null && f?.ipo?.atr_pct_max != null ? `${f.ipo.atr_pct_min}–${f.ipo.atr_pct_max}%` : '1.5–12%', src: "weekly_qualification_scan.py · FILTERS['ipo']['atr_pct_min/max']" },
        { name: 'Overnight Gap (20d)', threshold: f?.ipo?.avg_gap_pct_max    != null ? `≤ ${f.ipo.avg_gap_pct_max}%`   : '≤ 4.0%',          src: "weekly_qualification_scan.py · FILTERS['ipo']['avg_gap_pct_max']" },
        { name: 'Security Type',       threshold: 'Common stock only',                                                                          src: "weekly_qualification_scan.py · VALID_STOCK_TYPES" },
      ],
    },
    commod: {
      count: 'Fixed universe — 13 instruments',
      filters: [
        { name: 'GLD, SLV, PALL',        threshold: 'Precious metals',              src: 'weekly_qualification_scan.py · COMMOD_BASE_LIST' },
        { name: 'GDX, GDXJ, SIL',        threshold: 'Gold / silver miners',         src: 'weekly_qualification_scan.py · COMMOD_BASE_LIST' },
        { name: 'USO, BNO',               threshold: 'Crude oil (WTI + Brent)',      src: 'weekly_qualification_scan.py · COMMOD_BASE_LIST' },
        { name: 'DBA',                    threshold: 'Agriculture',                  src: 'weekly_qualification_scan.py · COMMOD_BASE_LIST' },
        { name: 'CPER, COPX, LIT, REMX', threshold: 'Industrial / strategic metals',src: 'weekly_qualification_scan.py · COMMOD_BASE_LIST (D041)' },
      ],
    },
    crypto: {
      count: 'Fixed universe — 2 instruments',
      filters: [
        { name: 'IBIT', threshold: 'BTC spot ETF — primary',   src: 'weekly_qualification_scan.py · CRYPTO_BASE_LIST (D041)' },
        { name: 'ETHE', threshold: 'ETH spot ETF — secondary', src: 'weekly_qualification_scan.py · CRYPTO_BASE_LIST (D041)' },
      ],
    },
  }
}

// ─── Source-linked sleeve params builder ─────────────────────────────────────

interface SleeveParamRow {
  sleeve:       string
  alloc:        string
  allocSrc:     string
  scalar:       string
  atrMult:      string
  atrMultSrc:   string
  pctShare:     string
  pctShareSrc:  string
  posCap:       string
  note:         string
}

function buildSleeveParams(config: StrategyConfig | null): SleeveParamRow[] {
  const tgt   = config?.sleeve_target_pct
  const pct   = config?.percent_share
  const mults = config?.sleeve_multipliers

  function alloc(sl: string, fallback: string): string {
    return tgt?.[sl] != null ? `${(tgt[sl] * 100).toFixed(1).replace(/\.0$/, '')}%` : fallback
  }
  function pctShare(sl: string, fallback: string): string {
    return pct?.[sl] != null ? `${(pct[sl] * 100).toFixed(2).replace(/\.?0+$/, '')}%` : fallback
  }
  function atrMult(sl: string, fallback: string): string {
    return mults?.[sl] != null ? `${mults[sl].toFixed(1)}×` : fallback
  }

  return [
    {
      sleeve: 'ETF',   alloc: alloc('etf', '33.3%'),   allocSrc: "position_sizing.py · SLEEVE_TARGET_PCT['etf']",
      scalar: 'Stock/ETF scalar',
      atrMult: atrMult('etf', '1.5×'),   atrMultSrc: "update_stops.py · SLEEVE_MULTIPLIERS['etf']",
      pctShare: pctShare('etf', '1.5%'), pctShareSrc: "position_sizing.py · PERCENT_SHARE['etf']",
      posCap: 'min(3% NAV, 20% of sleeve)', note: 'ATR% > 2% → IPO sizing',
    },
    {
      sleeve: 'Stock', alloc: alloc('stock', '25%'), allocSrc: "position_sizing.py · SLEEVE_TARGET_PCT['stock']",
      scalar: 'Stock/ETF scalar',
      atrMult: atrMult('stock', '2.0×'), atrMultSrc: "update_stops.py · SLEEVE_MULTIPLIERS['stock']",
      pctShare: pctShare('stock', '0.75%'), pctShareSrc: "position_sizing.py · PERCENT_SHARE['stock']",
      posCap: 'min(1.5% NAV, 20% of sleeve)', note: '',
    },
    {
      sleeve: 'IPO',   alloc: alloc('ipo', '16.7%'),   allocSrc: "position_sizing.py · SLEEVE_TARGET_PCT['ipo']",
      scalar: 'IPO scalar',
      atrMult: atrMult('ipo', '2.0×'),   atrMultSrc: "update_stops.py · SLEEVE_MULTIPLIERS['ipo']",
      pctShare: pctShare('ipo', '2.0%'), pctShareSrc: "position_sizing.py · PERCENT_SHARE['ipo']",
      posCap: 'min(1.5% NAV, 20% of sleeve)', note: 'D080 per-position cap',
    },
    {
      sleeve: 'Commod',alloc: alloc('commod', '16.7%'),allocSrc: "position_sizing.py · SLEEVE_TARGET_PCT['commod']",
      scalar: 'Commod scalar',
      atrMult: atrMult('commod', '2.0×'),atrMultSrc: "update_stops.py · SLEEVE_MULTIPLIERS['commod']",
      pctShare: pctShare('commod', '1.5%'), pctShareSrc: "position_sizing.py · PERCENT_SHARE['commod']",
      posCap: 'min(3% NAV, 20% of sleeve)', note: '',
    },
    {
      sleeve: 'Crypto', alloc: alloc('crypto', '8.3%'), allocSrc: "position_sizing.py · SLEEVE_TARGET_PCT['crypto']",
      scalar: 'Crypto scalar',
      atrMult: atrMult('crypto', '2.0×'), atrMultSrc: "update_stops.py · SLEEVE_MULTIPLIERS['crypto']",
      pctShare: pctShare('crypto', '1.5%'), pctShareSrc: "position_sizing.py · PERCENT_SHARE['crypto']",
      posCap: 'min(3% NAV, 20% of sleeve)', note: '',
    },
  ]
}

// ─── Chevron SVG ─────────────────────────────────────────────────────────────

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      width="14" height="14" viewBox="0 0 14 14" fill="none"
      aria-hidden="true"
      style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 250ms ease', flexShrink: 0 }}
    >
      <path d="M3 5L7 9L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

// ─── Layer Shell ─────────────────────────────────────────────────────────────

interface LayerBadge {
  text: string
  tone?: 'green' | 'amber' | 'red' | 'plain'
}

interface LayerShellProps {
  num: number
  title: string
  badges: LayerBadge[]
  activeSleeve: SleeveName
  expanded: boolean
  onToggle: () => void
  children: React.ReactNode
}

function LayerShell({ num, title, badges, activeSleeve, expanded, onToggle, children }: LayerShellProps) {
  const sleeveBorderColor =
    activeSleeve === 'etf'    ? 'var(--sleeve-etf)' :
    activeSleeve === 'stock'  ? 'var(--sleeve-stock)' :
    activeSleeve === 'ipo'    ? 'var(--sleeve-ipo)' :
    activeSleeve === 'commod' ? 'var(--sleeve-commod)' :
    activeSleeve === 'crypto' ? 'var(--sleeve-crypto)' :
    'var(--border-subtle)'

  return (
    <div
      className="playbook-layer"
      style={{ borderLeft: `2px solid ${sleeveBorderColor}` }}
    >
      <button
        className="playbook-layer-header"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={`playbook-layer-${num}-content`}
      >
        <span className="playbook-layer-num" aria-hidden="true">{num}</span>
        <span className="playbook-layer-title">{title}</span>
        <span className="playbook-layer-badges">
          {badges.map((b, i) => (
            <span
              key={i}
              className={`playbook-badge${b.tone && b.tone !== 'plain' ? ` playbook-badge--${b.tone}` : ' playbook-badge--plain'}`}
            >
              {b.text}
            </span>
          ))}
        </span>
        <ChevronIcon expanded={expanded} />
      </button>

      <div
        className={`playbook-layer-content-outer${expanded ? ' is-expanded' : ''}`}
        id={`playbook-layer-${num}-content`}
      >
        {/* @ts-expect-error inert is a valid HTML attribute not yet in all React type versions */}
        <div className="playbook-layer-content-inner" inert={!expanded ? '' : undefined}>
          {children}
        </div>
      </div>
    </div>
  )
}

// ─── Layer 1: Market Regime ───────────────────────────────────────────────────

function Layer1Content({ snapshot, regimeState }: { snapshot: PortfolioSnapshot | null; regimeState: RegimeState | null }) {
  const dd = snapshot?.drawdown_pct ?? 0
  const cb = cbScaleFromDrawdown(dd)
  const dailyPnlPct = snapshot?.daily_pnl_pct ?? 0
  const equity = snapshot?.net_worth ?? 0
  const cash = snapshot?.cash ?? 0
  const cashPct = equity > 0 ? (cash / equity * 100) : 0
  const minCashPct = cb.cashFloor
  const minCashAmt = equity * minCashPct / 100

  const stockScalar  = regimeState?.regime_scalar ?? null
  const ipoScalar    = regimeState?.ipo_scalar ?? null
  const commodScalar = regimeState?.commod_scalar ?? null
  const cryptoScalar = regimeState?.crypto_scalar ?? null
  const spyAbove200  = regimeState?.spy_above_200sma
  const breadthScore = regimeState?.breadth_score
  const entryCount   = regimeState?.entry_count
  const universeCount = regimeState?.universe_count
  const ipoRs        = regimeState?.ipo_rs_vs_spy
  const commodBreadth = regimeState?.commod_breadth
  const updatedAt     = regimeState?.computed_at
    ? (() => { const d = new Date(regimeState.computed_at); return isToday(d) ? format(d, 'HH:mm') : format(d, 'MMM d HH:mm') })()
    : null

  const CB_SCALE_ROWS = [
    { dd: '0–10%',  scale: '1.00×', label: 'Normal' },
    { dd: '10–15%', scale: '0.75×', label: 'Scale-down' },
    { dd: '15–20%', scale: '0.50×', label: 'Scale-down' },
    { dd: '20–25%', scale: '0.25×', label: 'Scale-down' },
    { dd: '25%+',   scale: '0×',    label: 'HALTED' },
  ]

  function activeDdRow(): number {
    if (dd >= 25) return 4
    if (dd >= 20) return 3
    if (dd >= 15) return 2
    if (dd >= 10) return 1
    return 0
  }
  const activeRow = activeDdRow()

  const sleeveBRows = [
    { label: 'Stock / ETF', scalar: stockScalar, tier: stockTierLabel(stockScalar), detail: breadthScore != null && entryCount != null && universeCount != null ? `Breadth ${breadthScore.toFixed(0)}% (${entryCount}/${universeCount})` : breadthScore != null ? `Breadth ${breadthScore.toFixed(0)}%` : '—' },
    { label: 'IPO',         scalar: ipoScalar,   tier: ipoTierLabel(ipoScalar),     detail: ipoRs != null ? `RS vs SPY: ${ipoRs >= 0 ? '+' : ''}${ipoRs.toFixed(1)}%` : '—' },
    { label: 'Commod',      scalar: commodScalar,tier: commodTierLabel(commodScalar),detail: commodBreadth != null ? `Breadth ${commodBreadth.toFixed(0)}%` : '—' },
    { label: 'Crypto',      scalar: cryptoScalar,tier: cryptoTierLabel(cryptoScalar),detail: 'Ref: IBIT' },
  ]

  return (
    <div className="playbook-layer-body">

      {/* Section A — Circuit Breaker */}
      <div className="playbook-section">
        <div className="playbook-section-title">System A — Circuit Breaker Scale (reactive)</div>

        <div className="playbook-row-group">
          <div className="playbook-kv-row">
            <span className="playbook-kv-label">CB Scale</span>
            <span className={`playbook-scalar tone-${scalarTone(cb.scalar)}`}>
              {fmtScalar(cb.scalar)}
            </span>
            <span className="playbook-kv-detail">{cb.label}</span>
          </div>
          <div className="playbook-kv-row">
            <span className="playbook-kv-label">Drawdown</span>
            <span className={dd >= 10 ? 'playbook-kv-value--warn' : 'playbook-kv-value'}>{fmtPct(-dd)}</span>
            <span className="playbook-kv-detail">from peak equity</span>
          </div>
          <div className="playbook-kv-row">
            <span className="playbook-kv-label">Daily P&amp;L</span>
            <span className={dailyPnlPct <= -4 ? 'playbook-kv-value--neg' : 'playbook-kv-value'}>{fmtPct(dailyPnlPct)}</span>
            <span className="playbook-kv-detail">halt at −4% · reduce at −6%</span>
          </div>
          <div className="playbook-kv-row">
            <span className="playbook-kv-label">Min cash</span>
            <span className="playbook-kv-value">{minCashPct}% ({fmtMoney(minCashAmt)})</span>
            <span className="playbook-kv-detail">actual {fmtPct(cashPct)}</span>
          </div>
        </div>

        <table className="playbook-table">
          <thead>
            <tr>
              <th>Drawdown</th>
              <th>Scale</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {CB_SCALE_ROWS.map((r, i) => (
              <tr key={r.dd} className={i === activeRow ? 'playbook-table-row--active' : ''}>
                <td className="font-mono">{r.dd}</td>
                <td className="font-mono">{r.scale}</td>
                <td>{r.label}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Section B — Per-Sleeve Scalars */}
      <div className="playbook-section">
        <div className="playbook-section-title">System B — Entry Sizing Scalars (proactive, per sleeve)</div>

        {spyAbove200 === false && (
          <div className="playbook-alert playbook-alert--warn">
            SPY below 200d SMA — scalars above 1.00× capped at 1.00× (broad-market downtrend confirmation)
          </div>
        )}

        {regimeState ? (
          <table className="playbook-table">
            <thead>
              <tr>
                <th>Sleeve</th>
                <th>Scalar</th>
                <th>Tier</th>
                <th>Signal</th>
              </tr>
            </thead>
            <tbody>
              {sleeveBRows.map(r => (
                <tr key={r.label}>
                  <td>{r.label}</td>
                  <td>
                    <span className={`playbook-scalar tone-${scalarTone(r.scalar)}`}>
                      {fmtScalar(r.scalar)}
                    </span>
                  </td>
                  <td>{r.tier}</td>
                  <td className="playbook-td-detail">{r.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="playbook-empty">No live regime data — pending pre-market run.</div>
        )}

        {spyAbove200 != null && (
          <div className="playbook-footer-row">
            <span className="playbook-kv-label">SPY vs 200d SMA:</span>
            <span className={spyAbove200 ? 'playbook-flag--pass' : 'playbook-flag--fail'}>
              {spyAbove200 ? '✓ above' : '✗ below'}
            </span>
            {updatedAt && <span className="playbook-kv-detail">Updated {updatedAt} ET</span>}
          </div>
        )}
      </div>

      {/* Section C: range gate removed (D079); the numbers are diagnostic only */}
      <div className="playbook-section">
        <div className="playbook-section-title">System C: Dynamic Range Gate removed (D079)</div>
        <div className="playbook-hint">
          No sleeve gates entries on one-bar range any more. The range_gate_* values the regime job still
          publishes are diagnostic only and do not affect any order.
        </div>
      </div>
    </div>
  )
}

// ─── Layer 2: Universe Filter ─────────────────────────────────────────────────

function Layer2Content({ activeSleeve, strategyConfig }: { activeSleeve: SleeveName; strategyConfig?: StrategyConfig | null }) {
  const atrPeriod = strategyConfig?.atr_period ?? 14
  const universeData = buildUniverseFilters(strategyConfig ?? null, atrPeriod)
  const isLive = strategyConfig != null
  const etfUniverse = strategyConfig?.etf_universe

  const toShow = activeSleeve === 'all'
    ? (['etf', 'stock', 'ipo', 'commod', 'crypto'] as const)
    : ([activeSleeve] as const)

  return (
    <div className="playbook-layer-body">
      <div className="playbook-hint-row">
        <span className="playbook-hint">Weekly scan (Sunday 8 PM ET). Structural filters run before technical qualification.</span>
        {isLive
          ? <span className="playbook-config-status playbook-config-status--live">● values sourced live from Python</span>
          : <span className="playbook-config-status">⚠ static fallback — run publish_strategy_config.py to source live</span>
        }
      </div>
      {toShow.map(sl => {
        const u = universeData[sl]
        if (!u) return null
        return (
          <div key={sl} className="playbook-section">
            <div className="playbook-section-title" style={{ textTransform: 'capitalize' }}>
              {sl} Sleeve — {u.count}
            </div>
            <table className="playbook-table">
              <thead>
                <tr><th>Filter</th><th>Threshold</th></tr>
              </thead>
              <tbody>
                {u.filters.map(f => (
                  <tr key={f.name} title={`Python: ${f.src}`}>
                    <td>{f.name}</td>
                    <td className="font-mono">{f.threshold}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sl === 'etf' && etfUniverse && etfUniverse.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div className="playbook-td-detail" style={{ marginBottom: 4 }} title="Python: scripts/auto_execute_etf.py · ETF_UNIVERSE (P22k D063 sector basket)">
                  Sector basket ({etfUniverse.length} symbols):
                </div>
                <div className="font-mono" style={{ fontSize: '0.82em', lineHeight: 1.7, color: 'var(--text-secondary)' }}>
                  {etfUniverse.join('  ·  ')}
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Layer 3: Technical Qualification ────────────────────────────────────────

const TECH_FILTERS = [
  { name: 'Price > 50d SMA',         rule: 'Required all sleeves',      benchmark: '—' },
  { name: '20d ROC > Benchmark',     rule: 'Required all sleeves',      benchmark: 'SPY (ETF/IPO) · Sector ETF (Stock)' },
  { name: '60d ROC > 0',             rule: 'Required all sleeves',      benchmark: '—' },
  { name: 'Close > 20-period VWAP',  rule: 'Required all sleeves',      benchmark: '—' },
]

function Layer3Content({ signals, activeSleeve: _activeSleeve, strategyConfig }: { signals: SignalState[]; activeSleeve: SleeveName; strategyConfig?: StrategyConfig | null }) {
  const filteredSignals = signals
  const totalPass = filteredSignals.filter(s => s.trend_qual).length
  const total     = filteredSignals.length
  const gates     = strategyConfig?.p22k_gate_params

  return (
    <div className="playbook-layer-body">
      <div className="playbook-section">
        <div className="playbook-section-title">Four required gates — all must pass</div>
        <table className="playbook-table">
          <thead>
            <tr><th>Filter</th><th>Rule</th><th>Benchmark</th></tr>
          </thead>
          <tbody>
            {TECH_FILTERS.map(f => (
              <tr key={f.name}>
                <td>{f.name}</td>
                <td className="playbook-td-detail">{f.rule}</td>
                <td className="playbook-td-detail font-mono">{f.benchmark}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="playbook-section">
        <div className="playbook-section-title" title="Python: scripts/utils/p22k_gates.py (P22k D063)">P22k Entry Gates — applied after TrendQual</div>
        <table className="playbook-table">
          <thead>
            <tr><th>Gate</th><th>Rule</th><th>Sleeves</th></tr>
          </thead>
          <tbody>
            <tr title="Python: p22k_gates.py · adx_accelerating()">
              <td>ADX Acceleration</td>
              <td className="font-mono playbook-td-detail">ADX({gates?.adx_period ?? 14}) slope today &gt; slope yesterday</td>
              <td className="playbook-td-detail">{gates?.sleeves_with_adx_gate?.join(', ') ?? 'all sleeves'}</td>
            </tr>
            <tr title="Python: p22k_gates.py · apply_weekly_rs_gate()">
              <td>Weekly RS Percentile</td>
              <td className="font-mono playbook-td-detail">Top {gates?.rs_gate_pct != null ? `${Math.round(gates.rs_gate_pct * 100)}%` : '20%'} by RS diff — set Monday, held all week</td>
              <td className="playbook-td-detail">{gates?.sleeves_with_rs_gate?.join(', ') ?? 'stock'}</td>
            </tr>
            <tr title="Python: p22k_gates.py · get_cooldown_symbols()">
              <td>Re-entry Cooldown</td>
              <td className="font-mono playbook-td-detail">Block re-entry {gates?.cooldown_days ?? 2}d after stop exit</td>
              <td className="playbook-td-detail">{gates?.sleeves_with_cooldown?.join(', ') ?? 'all sleeves'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {signals.length > 0 && (
        <div className="playbook-section">
          <div className="playbook-section-title">Live — today's signal universe</div>
          <div className="playbook-kv-row">
            <span className="playbook-kv-label">TrendQual pass</span>
            <span className="playbook-kv-value">{totalPass} / {total}</span>
            <span className="playbook-kv-detail">instruments qualifying today</span>
          </div>
          <div className="playbook-hint">Top disqualification reasons: BELOW_VWAP20, RS_WEAK_20D, BELOW_SMA50, ROC_60D_NEG</div>
        </div>
      )}
    </div>
  )
}

// ─── Layer 4: Entry Trigger ───────────────────────────────────────────────────

function Layer4Content({ signals, sleeveMap, activeSleeve }: {
  signals: SignalState[]
  positions?: Position[]
  sleeveMap: Record<string, string>
  activeSleeve: SleeveName
}) {
  const triggered = signals.filter(s => s.entry_signal != null)
  const filteredTriggered = activeSleeve === 'all' ? triggered : triggered.filter(s => {
    const sleeve = sleeveMap[s.ticker]
    return sleeve === activeSleeve
  })

  return (
    <div className="playbook-layer-body">
      <div className="playbook-section">
        <div className="playbook-section-title">Signal types</div>
        <table className="playbook-table">
          <thead>
            <tr><th>Signal</th><th>Description</th></tr>
          </thead>
          <tbody>
            <tr><td className="font-mono">Trnd-LEntry</td><td>Trend-following long entry — price breakout on qualifying instrument</td></tr>
            <tr><td className="font-mono">NewHi</td><td>New high breakout — closing at fresh multi-month high</td></tr>
            <tr><td className="font-mono">MomentumBreakout</td><td>Volume-confirmed momentum acceleration</td></tr>
          </tbody>
        </table>
      </div>

      <div className="playbook-section">
        <div className="playbook-section-title">Sleeve rebalancer</div>
        <div className="playbook-hint">
          When a new signal fires and the sleeve is at or near its capital target, the system executes a pro-rata trim of existing sleeve positions to make room. Atomicity contract: cancel old stop → market sell trim → replace stop. If any step fails, new entry is NOT placed.
        </div>
      </div>

      {filteredTriggered.length > 0 ? (
        <div className="playbook-section">
          <div className="playbook-section-title">Live — triggered today</div>
          <table className="playbook-table">
            <thead>
              <tr>
                <th>Ticker</th>
                <th>Sleeve</th>
                <th>Signal</th>
                <th>RS Diff</th>
              </tr>
            </thead>
            <tbody>
              {filteredTriggered.map(s => (
                <tr key={s.ticker}>
                  <td className="font-mono">{s.ticker}</td>
                  <td style={{ textTransform: 'capitalize' }}>{sleeveMap[s.ticker] ?? '—'}</td>
                  <td className="font-mono">{s.entry_signal}</td>
                  <td className={`font-mono ${s.rs_diff != null && s.rs_diff > 0 ? 'playbook-val--pos' : 'playbook-val--neg'}`}>
                    {s.rs_diff != null ? `${s.rs_diff >= 0 ? '+' : ''}${s.rs_diff.toFixed(1)}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="playbook-section">
          <div className="playbook-empty">No entry signals triggered today{activeSleeve !== 'all' ? ` for ${activeSleeve} sleeve` : ''}.</div>
        </div>
      )}
    </div>
  )
}

// ─── Layer 5: Position Sizing ─────────────────────────────────────────────────

function Layer5Content({ snapshot, regimeState, activeSleeve, strategyConfig }: {
  snapshot: PortfolioSnapshot | null
  regimeState: RegimeState | null
  activeSleeve: SleeveName
  strategyConfig?: StrategyConfig | null
}) {
  const equity = snapshot?.net_worth ?? 0
  const cb = cbScaleFromDrawdown(snapshot?.drawdown_pct ?? 0)

  const sleeveScalars: Record<string, number | null> = {
    etf:    regimeState?.regime_scalar ?? null,
    stock:  regimeState?.regime_scalar ?? null,
    ipo:    regimeState?.ipo_scalar ?? null,
    commod: regimeState?.commod_scalar ?? null,
    crypto: (regimeState as any)?.crypto_scalar ?? null,
  }

  const activeSleeveScalar = activeSleeve !== 'all' ? (sleeveScalars[activeSleeve] ?? 1.0) : (regimeState?.regime_scalar ?? 1.0)
  const effectiveCapital = equity * activeSleeveScalar * cb.scalar

  const isAboveEquity = effectiveCapital > equity
  const isBelowEquity = effectiveCapital < equity

  const SLEEVE_PARAMS = buildSleeveParams(strategyConfig ?? null)
  const isLive = strategyConfig != null

  const displayParams = activeSleeve === 'all'
    ? SLEEVE_PARAMS
    : SLEEVE_PARAMS.filter(p => p.sleeve.toLowerCase() === activeSleeve)

  return (
    <div className="playbook-layer-body">

      {/* Sizing equation */}
      <div className="playbook-section">
        <div className="playbook-section-title">Effective capital — live calculation</div>
        <div className="playbook-sizing-equation">
          <div className="playbook-sizing-operand">
            <span className="playbook-sizing-label">Equity</span>
            <span className="playbook-sizing-value">{fmtMoney(equity)}</span>
          </div>
          <span className="playbook-sizing-op">×</span>
          <div className="playbook-sizing-operand">
            <span className="playbook-sizing-label">Regime Scale</span>
            <span
              className="playbook-sizing-value"
              style={{ color: isAboveEquity ? 'var(--positive)' : isBelowEquity ? 'var(--warning)' : 'var(--text-primary)' }}
            >
              {fmtScalar(activeSleeveScalar)}
            </span>
          </div>
          <span className="playbook-sizing-op">×</span>
          <div className="playbook-sizing-operand">
            <span className="playbook-sizing-label">CB Scale</span>
            <span
              className="playbook-sizing-value"
              style={{ color: cb.scalar < 1.0 ? 'var(--warning)' : 'var(--text-primary)' }}
            >
              {fmtScalar(cb.scalar)}
            </span>
          </div>
          <span className="playbook-sizing-op">=</span>
          <div className="playbook-sizing-operand">
            <span className="playbook-sizing-label">Effective Capital</span>
            <span
              className="playbook-sizing-value"
              style={{ color: isAboveEquity ? 'var(--positive)' : isBelowEquity ? 'var(--negative)' : 'var(--text-primary)' }}
            >
              {fmtMoney(effectiveCapital)}
            </span>
          </div>
        </div>
        <div className="playbook-hint">shares = dual_cap(capital=effective_capital, ATR=ATR14, mult=sleeve_mult), then capped at min(1.5% NAV stock/ipo or 3% NAV etf/commod/crypto, 20% of regime-scaled sleeve target) (D080)</div>
        {activeSleeve !== 'all' && <div className="playbook-hint" style={{ color: `var(--sleeve-${activeSleeve})` }}>Showing {activeSleeve} sleeve scalar</div>}
      </div>

      {/* Per-sleeve params */}
      <div className="playbook-section">
        <div className="playbook-section-title-row">
          <span className="playbook-section-title">Per-sleeve parameters</span>
          {isLive
            ? <span className="playbook-config-status playbook-config-status--live">● sourced live from Python</span>
            : <span className="playbook-config-status">⚠ static fallback — run publish_strategy_config.py</span>
          }
        </div>
        <table className="playbook-table">
          <thead>
            <tr>
              <th>Sleeve</th>
              <th>% NAV</th>
              <th>Scalar</th>
              <th>ATR Mult</th>
              <th>% Share</th>
              <th>Position cap (D080)</th>
            </tr>
          </thead>
          <tbody>
            {displayParams.map(p => (
              <tr key={p.sleeve}>
                <td>{p.sleeve}</td>
                <td className="font-mono" title={`Python: ${p.allocSrc}`}>{p.alloc}</td>
                <td className="playbook-td-detail">{p.scalar}</td>
                <td className="font-mono" title={`Python: ${p.atrMultSrc}`}>{p.atrMult}</td>
                <td className="font-mono" title={`Python: ${p.pctShareSrc}`}>{p.pctShare}</td>
                <td className="font-mono">{p.posCap}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="playbook-hint">Sleeve targets sum to 100% of NAV. Regime scalars scale each target; the D080 cap applies to the scaled target.</div>
      </div>
    </div>
  )
}

// ─── Layer 6: Stop Management ─────────────────────────────────────────────────

function Layer6Content({ positions, activeSleeve, strategyConfig }: { positions: Position[]; activeSleeve: SleeveName; strategyConfig?: StrategyConfig | null }) {
  const atrPeriod   = strategyConfig?.atr_period ?? 14
  const barsBack    = strategyConfig?.bars_back  ?? 30
  const mults       = strategyConfig?.sleeve_multipliers
  const baseActiv   = (strategyConfig as Record<string, unknown>)?.sleeve_base_activation as Record<string, number> | undefined
  const etfMult     = mults?.etf    != null ? `${mults.etf.toFixed(1)}×`    : '1.5×'
  const stockMult   = mults?.stock  != null ? `${mults.stock.toFixed(1)}×`  : '2.0×'
  const ipoMult     = mults?.ipo    != null ? `${mults.ipo.toFixed(1)}×`    : '2.0×'
  const commodMult  = mults?.commod != null ? `${mults.commod.toFixed(1)}×` : '2.0×'
  const cryptoMult  = mults?.crypto != null ? `${mults.crypto.toFixed(1)}×` : '2.0×'
  // Activation multiplier — uniform across sleeves in P22k (D063); use etf as representative, fallback 3.0
  const activMult   = baseActiv?.etf ?? baseActiv?.stock ?? 3.0
  const activLabel  = `${activMult.toFixed(0)}×`
  const atrLabel    = `ATR${atrPeriod}`
  const filtered = activeSleeve === 'all'
    ? positions
    : positions.filter(p => p.sleeve === activeSleeve)

  const sorted = [...filtered]
    .filter(p => p.stop_price != null && p.current_price != null)
    .sort((a, b) => {
      const pA = percentToStop(a) ?? 999
      const pB = percentToStop(b) ?? 999
      if (pA !== pB) return pA - pB
      return a.ticker.localeCompare(b.ticker)
    })

  function urgencyStyle(pct: number | null): React.CSSProperties {
    if (pct == null) return {}
    if (pct < 3)  return { color: 'var(--negative)', fontWeight: 700 }
    if (pct < 7)  return { color: 'var(--warning)',  fontWeight: 700 }
    return {}
  }

  return (
    <div className="playbook-layer-body">
      {/* Three-part stop logic summary */}
      <div className="playbook-section">
        <div className="playbook-section-title">Stop system — three sequential gates</div>
        <div className="playbook-stop-logic">
          <div className="playbook-stop-step">
            <span className="playbook-stop-step-num">①</span>
            <div>
              <div className="playbook-stop-step-title">Entry Stop — placed simultaneously with entry</div>
              <div className="playbook-stop-step-detail font-mono" title="Python: scripts/update_stops.py · ATR_PERIOD, SLEEVE_MULTIPLIERS">
                stop = entry_price − ATR_mult × {atrLabel}
              </div>
              <div className="playbook-stop-step-detail font-mono">floor = max(computed_stop, entry_price × 0.92)</div>
              <div className="playbook-stop-step-detail" title={`Python: scripts/update_stops.py · SLEEVE_MULTIPLIERS (BARS_BACK=${barsBack})`}>
                ATR mult: {etfMult} (ETF) · {commodMult} (Commod) · {stockMult} (Stock) · {ipoMult} (IPO) · {cryptoMult} (Crypto) · Placed as GTC StopMarket
              </div>
            </div>
          </div>
          <div className="playbook-stop-step">
            <span className="playbook-stop-step-num">②</span>
            <div>
              <div className="playbook-stop-step-title">{activLabel} Activation Gate — trailing only fires when:</div>
              <div className="playbook-stop-step-detail font-mono">live_price ≥ avg_cost + {activLabel} × (avg_cost − initial_stop)</div>
              <div className="playbook-stop-step-detail" title="Python: scripts/update_stops.py · SLEEVE_BASE_ACTIVATION (P22k D063)">Position must be profitable by ≥ {activLabel} initial risk per share</div>
            </div>
          </div>
          <div className="playbook-stop-step">
            <span className="playbook-stop-step-num">③</span>
            <div>
              <div className="playbook-stop-step-title">Trailing Ratchet — runs every 30 min (9:35–15:55 ET)</div>
              <div className="playbook-stop-step-detail font-mono" title={`Python: scripts/update_stops.py · ATR_PERIOD=${atrPeriod}, SLEEVE_MULTIPLIERS`}>
                new_stop = anchored_VWAP − ATR_mult × {atrLabel}
              </div>
              <div className="playbook-stop-step-detail font-mono">anchored_VWAP = VWAP from entry_date to today</div>
              <div className="playbook-stop-step-detail">Fires only when new_stop &gt; current_stop AND new_stop &gt; initial_stop — stops NEVER move down</div>
            </div>
          </div>
        </div>
      </div>

      {/* Open positions stop table */}
      <div className="playbook-section">
        <div className="playbook-section-title">
          Open positions — sorted by proximity to stop
          {activeSleeve !== 'all' && <span style={{ color: `var(--sleeve-${activeSleeve})`, marginLeft: 8, textTransform: 'capitalize' }}>{activeSleeve} sleeve</span>}
        </div>

        {sorted.length === 0 ? (
          <div className="playbook-empty">No open positions{activeSleeve !== 'all' ? ` in ${activeSleeve} sleeve` : ''}.</div>
        ) : (
          <table className="playbook-table playbook-stop-table">
            <thead>
              <tr>
                <th>Ticker</th>
                <th>% to Stop</th>
                <th>Stop</th>
                <th className="playbook-col-hide-narrow">Current</th>
                <th className="playbook-col-hide-narrow">Sleeve</th>
                <th className="playbook-col-hide-wide">Entry</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(p => {
                const pct = percentToStop(p)
                return (
                  <tr key={p.ticker}>
                    <td className="font-mono">{p.ticker}</td>
                    <td className="font-mono" style={urgencyStyle(pct)}>
                      {pct != null ? `${pct.toFixed(1)}%` : '—'}
                    </td>
                    <td className="font-mono">${p.stop_price?.toFixed(2) ?? '—'}</td>
                    <td className="font-mono playbook-col-hide-narrow">${p.current_price?.toFixed(2) ?? '—'}</td>
                    <td className="playbook-col-hide-narrow" style={{ textTransform: 'capitalize', color: p.sleeve ? `var(--sleeve-${p.sleeve})` : undefined }}>{p.sleeve ?? '—'}</td>
                    <td className="font-mono playbook-col-hide-wide">${p.avg_cost?.toFixed(2) ?? '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
        <div className="playbook-hint">
          <span style={{ color: 'var(--negative)', fontWeight: 700 }}>Bold red</span> &lt;3% · <span style={{ color: 'var(--warning)', fontWeight: 700 }}>Bold amber</span> 3–7% · Normal &gt;7%
        </div>
      </div>
    </div>
  )
}

// ─── Layer badge builders ────────────────────────────────────────────────────

function layer1Badges(snapshot: PortfolioSnapshot | null, regimeState: RegimeState | null): LayerBadge[] {
  const dd    = snapshot?.drawdown_pct ?? 0
  const cb    = cbScaleFromDrawdown(dd)
  const stock = regimeState?.regime_scalar ?? null
  const tone  = scalarTone(stock)
  const cbOk  = cb.scalar >= 1.0

  if (!cbOk && (stock != null && stock < 1.0)) {
    return [{ text: `⚠ CB + ${stockTierLabel(stock)}`, tone: 'red' }]
  }
  if (!cbOk) {
    return [{ text: `⚠ CB ${fmtScalar(cb.scalar)}`, tone: 'amber' }]
  }
  if (tone === 'strong-bull' || tone === 'bull') {
    return [{ text: `${fmtScalar(stock)} ${stockTierLabel(stock)}`, tone: 'green' }]
  }
  return [{ text: `${fmtScalar(stock)} Neutral`, tone: 'plain' }]
}

function layer2Badges(activeSleeve: SleeveName): LayerBadge[] {
  const counts: Record<string, string> = {
    all: '~188 qualified', etf: '~37 qualified', stock: '~147 qualified',
    ipo: '~4–8 qualified', commod: '13 fixed', crypto: '2 fixed',
  }
  return [{ text: counts[activeSleeve] ?? '—', tone: 'plain' }]
}

function layer3Badges(signals: SignalState[]): LayerBadge[] {
  if (signals.length === 0) return [{ text: '4 checks', tone: 'plain' }]
  const pass = signals.filter(s => s.trend_qual).length
  return [{ text: `${pass}/${signals.length} pass`, tone: 'plain' }]
}

function layer4Badges(signals: SignalState[]): LayerBadge[] {
  const n = signals.filter(s => s.entry_signal != null).length
  if (n === 0) return [{ text: '0 signals today', tone: 'plain' }]
  return [{ text: `${n} signal${n !== 1 ? 's' : ''} today`, tone: 'green' }]
}

function layer5Badges(snapshot: PortfolioSnapshot | null, regimeState: RegimeState | null): LayerBadge[] {
  const equity  = snapshot?.net_worth ?? 0
  const scalar  = regimeState?.regime_scalar ?? 1.0
  const cbScale = cbScaleFromDrawdown(snapshot?.drawdown_pct ?? 0).scalar
  const eff     = equity * scalar * cbScale
  if (equity === 0) return [{ text: 'No data', tone: 'plain' }]
  return [{ text: `Eff. cap: ${fmtMoney(eff)}`, tone: 'plain' }]
}

function layer6Badges(positions: Position[]): LayerBadge[] {
  const nearStop = positions.filter(p => {
    const pct = percentToStop(p)
    return pct != null && pct < 7
  }).length
  const primary: LayerBadge = { text: `${positions.length} position${positions.length !== 1 ? 's' : ''}`, tone: 'plain' }
  if (nearStop === 0) return [primary]
  return [primary, { text: `${nearStop} near stop`, tone: nearStop > 0 ? 'amber' : 'plain' }]
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function StrategyPlaybook({ snapshot, regimeState, signals, positions, sleeveMap, isSignalsStale, strategyConfig, onOpenStrategy }: Props) {
  const [isMinimized, setIsMinimized] = useState(true)
  const [activeSleeve, setActiveSleeve] = useState<SleeveName>('all')

  // Layer 6 default: expand if any position < 7% to stop
  const anyNearStop = useMemo(
    () => positions.some(p => { const pct = percentToStop(p); return pct != null && pct < 7 }),
    [positions]
  )

  const [expandedLayers, setExpandedLayers] = useState<Set<number>>(() => {
    const init = new Set<number>([1])
    if (anyNearStop) init.add(6)
    return init
  })

  function toggleLayer(n: number) {
    setExpandedLayers(prev => {
      const next = new Set(prev)
      if (next.has(n)) next.delete(n); else next.add(n)
      return next
    })
  }

  const isExpanded = (n: number) => expandedLayers.has(n)

  return (
    <div className="card playbook-card">
      <div className="card-header">
        <h2 className="card-title">Strategy Playbook</h2>
        <div className="playbook-header-actions">
          {isSignalsStale && (
            <span className="card-badge" style={{ background: 'var(--warning-bg)', color: 'var(--warning)' }}>
              ⚠ Signals stale
            </span>
          )}
          {onOpenStrategy && (
            <button type="button" className="card-badge playbook-ladders-link" onClick={onOpenStrategy}>
              Full entry ladders
            </button>
          )}
          <button
            className="sleeve-rules-toggle"
            onClick={() => setIsMinimized(v => !v)}
            aria-label={isMinimized ? 'Expand playbook' : 'Minimize playbook'}
            aria-expanded={!isMinimized}
            aria-controls="playbook-body"
          >
            <ChevronIcon expanded={!isMinimized} />
          </button>
        </div>
      </div>

      <div
        id="playbook-body"
        className={`playbook-body-collapse${!isMinimized ? ' is-expanded' : ''}`}
      >
        <div className="playbook-body-inner">
      {/* Sleeve filter — sticky */}
      <div className="playbook-filter-bar">
        <div
          role="radiogroup"
          aria-label="Sleeve filter"
          className="playbook-sleeve-filter"
        >
          {SLEEVE_OPTIONS.map(opt => (
            <button
              key={opt.id}
              role="radio"
              aria-checked={activeSleeve === opt.id}
              aria-label={opt.ariaLabel}
              className={`playbook-sleeve-pill${activeSleeve === opt.id ? ` playbook-sleeve-pill--${opt.id}` : ''}`}
              onClick={() => setActiveSleeve(opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Layer stack */}
      <div className="playbook-layers">
        <LayerShell num={1} title="Market Regime" activeSleeve={activeSleeve}
          badges={layer1Badges(snapshot, regimeState)}
          expanded={isExpanded(1)} onToggle={() => toggleLayer(1)}
        >
          <Layer1Content snapshot={snapshot} regimeState={regimeState} />
        </LayerShell>

        <LayerShell num={2} title="Universe Filter" activeSleeve={activeSleeve}
          badges={layer2Badges(activeSleeve)}
          expanded={isExpanded(2)} onToggle={() => toggleLayer(2)}
        >
          <Layer2Content activeSleeve={activeSleeve} strategyConfig={strategyConfig} />
        </LayerShell>

        <LayerShell num={3} title="Technical Qualification" activeSleeve={activeSleeve}
          badges={layer3Badges(signals)}
          expanded={isExpanded(3)} onToggle={() => toggleLayer(3)}
        >
          <Layer3Content signals={signals} activeSleeve={activeSleeve} strategyConfig={strategyConfig} />
        </LayerShell>

        <LayerShell num={4} title="Entry Trigger" activeSleeve={activeSleeve}
          badges={layer4Badges(signals)}
          expanded={isExpanded(4)} onToggle={() => toggleLayer(4)}
        >
          <Layer4Content signals={signals} positions={positions} sleeveMap={sleeveMap} activeSleeve={activeSleeve} />
        </LayerShell>

        <LayerShell num={5} title="Position Sizing" activeSleeve={activeSleeve}
          badges={layer5Badges(snapshot, regimeState)}
          expanded={isExpanded(5)} onToggle={() => toggleLayer(5)}
        >
          <Layer5Content snapshot={snapshot} regimeState={regimeState} activeSleeve={activeSleeve} strategyConfig={strategyConfig} />
        </LayerShell>

        <LayerShell num={6} title="Stop Management" activeSleeve={activeSleeve}
          badges={layer6Badges(positions)}
          expanded={isExpanded(6)} onToggle={() => toggleLayer(6)}
        >
          <Layer6Content positions={positions} activeSleeve={activeSleeve} strategyConfig={strategyConfig} />
        </LayerShell>
      </div>
        </div>
      </div>
    </div>
  )
}
